import hyRequest from '@/service'
import { localCache } from '@/utils/cache'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'

export type UploadScene =
  | 'avatar' // 头像上传
  | 'document' // 文档上传
  | 'image' // 图片上传
  | 'attachment' // 附件上传
  | string // 支持自定义场景

export interface FileItem {
  id: number | string
  configId: number | string
  path: string
  name: string
  url: string
  type: number
  size: number
  extension: string
  createTime: string
  scene?: UploadScene // 文件上传的场景
  fileKey: string // 关键key（必传，与组件fileKey对应）
  status?: string
  fileSize?: number
}

interface FileState {
  // 核心修改：currentFile 改为 { [fileKey]: FileItem }，按fileKey存储当前文件
  currentFile: Record<string, FileItem | null>
  fileList: FileItem[] // 文件列表（存储所有文件）
  // 上传加载状态：按 fileKey 区分（比按scene更精准）
  uploadLoading: Record<string, boolean>
  detailLoading: Record<string, boolean> // 详情加载状态（按fileKey）
  deleteLoading: Record<string, boolean> // 删除加载状态（按fileKey）
}

export const useFileStore = defineStore('file', {
  state: (): FileState => ({
    currentFile: {}, // 初始为空对象，动态添加fileKey对应的文件
    fileList: [],
    uploadLoading: {}, // 动态存储 fileKey: loading状态
    detailLoading: {},
    deleteLoading: {}
  }),
  getters: {
    // 获取文件列表总数
    fileListCount: (state) => state.fileList.length,
    // 根据ID快速获取文件
    getFileById: (state) => (fileId: number | string) =>
      state.fileList.find((item) => item.id === fileId) || null,
    // 根据 fileKey 获取对应文件（核心 getter）
    getFileByKey: (state) => (fileKey: string) =>
      state.fileList.find((item) => item.fileKey === fileKey) || null,
    // 根据 scene + fileKey 获取文件（双重精准匹配）
    getFileBySceneAndKey: (state) => (scene: UploadScene, fileKey: string) =>
      state.fileList.find(
        (item) => item.scene === scene && item.fileKey === fileKey
      ) || null,
    // 获取已上传文件的URL列表（可按fileKey过滤）
    getFileUrlByKey: (state) => (fileKey: string) => {
      const file = state.fileList.find((item) => item.fileKey === fileKey)
      return file ? file.url : ''
    },
    // 根据fileKey获取上传加载状态
    getUploadLoadingByKey:
      (state) =>
      (fileKey: string = 'default') =>
        state.uploadLoading[fileKey] || false
  },
  actions: {
    /**
     * 文件上传（核心优化：关联fileKey，独立存储状态）
     * @param file - 要上传的文件对象
     * @param scene - 上传场景
     * @param fileKey - 唯一标识（与组件prop对应）
     * @param params - 额外参数
     * @returns 上传成功的文件信息
     **/
    async uploadFile(
      file: File,
      scene: UploadScene,
      fileKey: string, // 改为必传，确保每个文件都有唯一key
      params?: Record<string, any>
    ) {
      if (!file) {
        ElMessage.error('还没有选择上传的文件')
        return null
      }
      if (!fileKey) {
        ElMessage.error('fileKey不能为空（用于区分不同上传项）')
        return null
      }

      try {
        this.uploadLoading[fileKey] = true // 按fileKey设置loading
        const formData = new FormData()
        formData.append('file', file)

        // 追加额外参数
        if (params) {
          Object.keys(params).forEach((key) => {
            const value = params[key]
            formData.append(key, value instanceof Blob ? value : String(value))
          })
        }
        const response = await hyRequest.post({
          url: 'app-api/file/file/upload',
          headers: { 'Content-Type': 'multipart/form-data' },
          data: formData,
          hideLoading: true
        })

        console.log(`[${fileKey}] 上传文件响应：`, response)
        if (response.code === 0 && response.data) {
          const fileData: FileItem = {
            ...(response.data as Omit<FileItem, 'scene' | 'fileKey'>),
            scene,
            fileKey,
            status: 'success'
          }

          // 1. 更新currentFile（按fileKey存储）
          this.currentFile[fileKey] = fileData
          // 2. 更新fileList（过滤同fileKey的旧文件，确保唯一）
          this.fileList = this.fileList.filter(
            (item) => item.fileKey !== fileKey
          )
          this.fileList.push(fileData)
          // 文件只保存在当前会话的 Pinia 中，持久化交给业务接口保存文件ID
          // ElMessage.success(`[${fileKey}] 文件上传成功`)
          return fileData
        }
      } catch (error: any) {
        console.error(`[${fileKey}] 文件上传失败：`, error)
        ElMessage.error(
          `[${fileKey}] 上传失败：${error.ElMessage || '网络错误，请重试'}`
        )
        return null
      } finally {
        this.uploadLoading[fileKey] = false // 按fileKey清除loading
      }
    },

    /**
     * 获取文件详情（按fileKey关联状态）
     * @param fileId - 文件ID
     * @param fileKey - 唯一标识
     * @returns 文件详情
     */
    async getFileDetail(fileId: number | string, fileKey: string) {
      // const ElMessage = useElMessage()
      if (!fileId || isNaN(Number(fileId))) {
        this.currentFile[fileKey] = null
        return null
      }
      if (!fileKey) {
        ElMessage.error('fileKey不能为空')
        return null
      }

      try {
        this.detailLoading[fileKey] = true // 按fileKey设置loading
        const response = await hyRequest.get({
          url: 'app-api/file/file/detail',
          params: { id: fileId },
          hideLoading: true
        })

        console.log(`[${fileKey}] 获取文件详情响应：`, response)
        if (response.code === 0 && response.data) {
          const fileData: FileItem = {
            ...(response.data as Omit<FileItem, 'fileKey'>),
            fileKey // 绑定fileKey
          }

          // 更新currentFile和fileList
          this.currentFile[fileKey] = fileData
          const index = this.fileList.findIndex((item) => item.id === fileId)
          if (index !== -1) {
            this.fileList[index] = fileData
          } else {
            this.fileList.push(fileData)
          }

          return fileData
        } else {
          ElMessage.error(
            `[${fileKey}] 获取文件详情失败：${response.msg || '未知错误'}`
          )
          this.currentFile[fileKey] = null
          return null
        }
      } catch (error: any) {
        console.error(`[${fileKey}] 获取文件详情失败：`, error)
        ElMessage.error(
          `[${fileKey}] 获取文件详情失败：${error.ElMessage || '网络错误'}`
        )
        this.currentFile[fileKey] = null
        return null
      } finally {
        this.detailLoading[fileKey] = false // 按fileKey清除loading
      }
    },

    /**
     * 删除文件（按fileKey清理状态） todo 后端暂未提供接口
     * @param fileId - 文件ID
     * @param fileKey - 唯一标识
     * @param scene - 上传场景
     * @returns 是否删除成功
     */
    // async deleteFile(
    //   fileId: number | string,
    //   fileKey: string,
    //   scene?: UploadScene
    // ) {
    //   const ElMessage = useElMessage()
    //   if (!fileId || !fileKey) return false

    //   try {
    //     this.deleteLoading[fileKey] = true // 按fileKey设置loading
    //     const cacheKey = `${fileKey}_${scene}_${fileId}`
    //     localCache.removeCache(cacheKey)
    //     this.currentFile[fileKey] = null
    //     this.fileList = this.fileList.filter(
    //       (item) => !(item.id === fileId && item.fileKey === fileKey)
    //     )
    //     // 3. 提示
    //     const sceneName = scene ? this.getSceneName(scene) : '文件'
    //     ElMessage.success(`[${fileKey}] ${sceneName}删除成功`)
    //     return true
    //     // const response = await hyRequest.delete({
    //     //   url: 'app-api/file/file/delete',
    //     //   params: { id: fileId }
    //     // })

    //     // if (response.code === 0) {
    //     //   // 1. 清除缓存
    //     //   const cacheKey = `${fileKey}_${scene}_${fileId}`
    //     //   localCache.removeCache(cacheKey)
    //     //   // 2. 清理状态
    //     //   this.currentFile[fileKey] = null
    //     //   this.fileList = this.fileList.filter(
    //     //     (item) => !(item.id === fileId && item.fileKey === fileKey)
    //     //   )
    //     //   // 3. 提示
    //     //   const sceneName = scene ? this.getSceneName(scene) : '文件'
    //     //   ElMessage.success(`[${fileKey}] ${sceneName}删除成功`)
    //     //   return true
    //     // } else {
    //     //   ElMessage.error(`[${fileKey}] 删除失败：${response.msg || '未知错误'}`)
    //     //   return false
    //     // }
    //   } catch (error: any) {
    //     console.error(`[${fileKey}] 删除文件失败：`, error)
    //     ElMessage.error(`[${fileKey}] 删除失败：${error.ElMessage || '网络错误'}`)
    //     return false
    //   } finally {
    //     this.deleteLoading[fileKey] = false // 按fileKey清除loading
    //   }
    // },
    async deleteFile(
      fileId: number | string,
      fileKey: string,
      scene?: UploadScene
    ) {
      // let ElMessage: ReturnType<typeof useElMessage> | null = null
      try {
        // ElMessage = useElMessage()
      } catch (msgError) {
        console.error(`[${fileKey}] 获取ElMessage实例失败：`, msgError)
        // ElMessage = {
        //   error: (text: string) => console.error(`错误：${text}`),
        //   success: (text: string) => console.log(`成功：${text}`),
        //   info: () => {},
        //   warning: () => {},
        //   loading: () => ({ close: () => {} })
        // } as ReturnType<typeof useElMessage>
      }

      if (!fileId || fileId === '' || !fileKey || fileKey === '') {
        ElMessage?.error(
          `[${fileKey}] 文件ID(${fileId})或fileKey(${fileKey})不能为空`
        )
        return false
      }

      try {
        this.deleteLoading = this.deleteLoading || {}
        this.currentFile = this.currentFile || {}
        this.fileList = Array.isArray(this.fileList) ? this.fileList : []

        this.deleteLoading[fileKey] = true

        // ========== 核心修改：使用Cache类封装的removeByPrefix方法 ==========
        const cachePrefix = `${fileKey}_${scene}_`
        try {
          // 直接调用localCache的removeByPrefix方法（批量删除前缀匹配的缓存）
          console.log(`[${fileKey}] 开始删除前缀为 "${cachePrefix}" 的缓存`)
          localCache.removeByPrefix(cachePrefix)

          // 验证删除结果（调试用，可上线后删除）
          const remainingKeys = localCache.getKeysByPrefix(cachePrefix)
          if (remainingKeys.length === 0) {
            console.log(
              `[${fileKey}] 缓存删除成功：前缀${cachePrefix}下无残留数据`
            )
          } else {
            console.warn(
              `[${fileKey}] 缓存删除不彻底，剩余key：`,
              remainingKeys
            )
          }
        } catch (cacheError) {
          console.error(`[${fileKey}] 缓存清理失败：`, cacheError)
        }

        // 清理store中的数据
        this.currentFile[fileKey] = null
        this.fileList = this.fileList.filter((item) => {
          if (!item) return true
          return !(item.id === fileId && item.fileKey === fileKey)
        })

        // 提示信息
        let sceneName = '文件'
        try {
          sceneName = scene ? this.getSceneName(scene) : '文件'
        } catch (sceneError) {
          console.warn(`[${fileKey}] 获取场景名称失败：`, sceneError)
        }
        ElMessage?.success(`[${fileKey}] ${sceneName}删除成功`)
        return true
      } catch (error: any) {
        const errorMsg = error?.ElMessage || error?.toString() || '未知错误'
        const errorStack = error?.stack || '无堆栈信息'
        console.error(`[${fileKey}] 删除文件核心逻辑报错：`, {
          errorMsg,
          errorStack,
          fileId,
          fileKey,
          scene
        })
        ElMessage?.error(`[${fileKey}] 删除失败：${errorMsg}`)
        return false
      } finally {
        try {
          if (this.deleteLoading) {
            this.deleteLoading[fileKey] = false
          }
        } catch (finallyError) {
          console.error(`[${fileKey}] 清除loading失败：`, finallyError)
        }
      }
    },
    /**
     * 按fileKey清空文件（单个上传项重置）
     * @param fileKey - 唯一标识
     */
    clearFileByKey(fileKey: string) {
      this.currentFile[fileKey] = null
      this.fileList = this.fileList.filter((item) => item.fileKey !== fileKey)
      // 清除对应loading状态
      delete this.uploadLoading[fileKey]
      delete this.detailLoading[fileKey]
      delete this.deleteLoading[fileKey]
      // ElMessage.success(`[${fileKey}] 文件已清空`)
    },

    /**
     * 按scene清空文件（批量重置）
     * @param scene - 上传场景
     */
    clearFilesByScene(scene: UploadScene) {
      // const ElMessage = useElMessage()
      const fileKeys = this.fileList
        .filter((item) => item.scene === scene)
        .map((item) => item.fileKey)

      // 清空对应fileKey的状态
      fileKeys.forEach((key) => {
        this.currentFile[key] = null
        delete this.uploadLoading[key]
        delete this.detailLoading[key]
        delete this.deleteLoading[key]
      })

      this.fileList = this.fileList.filter((item) => item.scene !== scene)
      ElMessage.success(`已清空${this.getSceneName(scene)}场景下所有文件`)
    },

    /**
     * 清空所有文件状态
     */
    resetFileState() {
      this.currentFile = {}
      this.fileList = []
      this.uploadLoading = {}
      this.detailLoading = {}
      this.deleteLoading = {}
    },

    clearAllFiles() {
      // const ElMessage = useElMessage()
      this.resetFileState()
      ElMessage.success('已清空所有文件状态')
    },

    /**
     * 从缓存加载文件（初始化回显用）
     * @param fileKey - 唯一标识
     * @param scene - 上传场景
     */
    loadFileFromCache(fileKey: string, scene: UploadScene) {
      // 使用 getKeysByPrefix 方法
      const cacheKeys = localCache.getKeysByPrefix(`${fileKey}_${scene}_`)
      if (cacheKeys.length > 0) {
        // 获取最新的一条缓存（按时间戳排序）
        const sortedKeys = cacheKeys.sort((a, b) => {
          const aTime = parseInt(a.split('_').pop() || '0')
          const bTime = parseInt(b.split('_').pop() || '0')
          return bTime - aTime
        })

        const fileData = localCache.getCacheWithType<FileItem>(sortedKeys[0])
        if (fileData) {
          this.currentFile[fileKey] = fileData
          // 同步到fileList
          this.fileList = this.fileList.filter(
            (item) => item.fileKey !== fileKey
          )
          this.fileList.push(fileData)
          console.log(`[${fileKey}] 从缓存加载文件：`, fileData)
        }
      }
    },

    /**
     * 获取场景名称（辅助方法）
     */
    getSceneName(scene: UploadScene): string {
      const sceneMap: Record<UploadScene, string> = {
        avatar: '头像',
        document: '文档',
        image: '图片',
        attachment: '附件'
      }
      return sceneMap[scene] || scene
    }
  }
})
